export class AuthApiError extends Error {
  constructor(readonly code: string, message: string, readonly status: number) {
    super(message);
  }
}

export type AuthSession = {
  user: { id: string; name?: string | null; email: string; image?: string | null };
  session?: { id?: string; expiresAt?: string };
};
export type SocialProvider = "google" | "microsoft";
export type AuthProviderCapabilities = { email: boolean; google: boolean; microsoft: boolean };
type AuthActionResult = { ok?: boolean; code?: string; url?: string };

export function authApiBaseUrl(): string {
  const configured = import.meta.env.VITE_AUTH_API_BASE_URL?.trim();
  if (configured) return configured.replace(/\/$/, "");
  if (window.location.hostname === "pdf.xhalo.co") return "https://auth.xhalo.co";
  if (window.location.hostname === "pdf-preview.xhalo.co" || window.location.hostname.endsWith("xhalo-document-preview.pages.dev")) {
    return "https://auth-image-preview.xhalo.co";
  }
  return "http://127.0.0.1:3000";
}

export function safeLocalReturnTo(value: string | null, fallback = "/"): string {
  if (!value) return fallback;
  const candidate = value.trim();
  let decoded = candidate;
  try {
    for (let index = 0; index <= candidate.length; index += 1) {
      if (!decoded.startsWith("/") || decoded.startsWith("//") || decoded.includes("\\") || /[\u0000-\u001f\u007f]/.test(decoded)) return fallback;
      const next = decodeURIComponent(decoded);
      if (next === decoded) break;
      if (index === candidate.length) return fallback;
      decoded = next;
    }
  } catch {
    return fallback;
  }
  if (!candidate.startsWith("/") || candidate.startsWith("//") || candidate.includes("\\") || /[\u0000-\u001f\u007f]/.test(candidate)) return fallback;
  const decodedPath = new URL(decoded, window.location.origin).pathname;
  if (/^\/(signin|auth)(\/|$)/.test(decodedPath)) return fallback;
  const parsed = new URL(candidate, window.location.origin);
  return parsed.origin === window.location.origin ? `${parsed.pathname}${parsed.search}${parsed.hash}` : fallback;
}

export function localSignInHref(returnTo: string, locale: string): string {
  return `/signin?${new URLSearchParams({ returnTo: safeLocalReturnTo(returnTo), lang: locale })}`;
}

function callbackUrl(path: "/auth/callback" | "/signin", returnTo: string, locale: string): URL {
  const url = new URL(path, window.location.origin);
  url.searchParams.set("returnTo", safeLocalReturnTo(returnTo));
  url.searchParams.set("lang", locale);
  return url;
}

async function authRequest(path: string, body: Record<string, unknown>, locale?: string): Promise<AuthActionResult> {
  const response = await fetch(`${authApiBaseUrl()}/api/auth${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json", "x-request-id": crypto.randomUUID(), ...(locale ? { "accept-language": locale } : {}) },
    body: JSON.stringify(body),
    signal: AbortSignal.timeout(30_000),
  });
  const payload = await response.json().catch(() => null) as { code?: string; message?: string; url?: string } | null;
  if (!response.ok) throw new AuthApiError(payload?.code || "AUTH_REQUEST_FAILED", payload?.message || "Authentication request failed.", response.status);
  if (!payload || typeof payload !== "object") throw new AuthApiError("AUTH_INVALID_RESPONSE", "Invalid authentication response.", 502);
  return payload;
}

async function authGet(path: string): Promise<Record<string, unknown>> {
  const response = await fetch(`${authApiBaseUrl()}/api/auth${path}`, {
    credentials: "include",
    headers: { "x-request-id": crypto.randomUUID() },
    signal: AbortSignal.timeout(15_000),
  });
  const payload = await response.json().catch(() => null) as Record<string, unknown> | null;
  if (!response.ok) throw new AuthApiError(String(payload?.code || "AUTH_REQUEST_FAILED"), String(payload?.message || "Authentication request failed."), response.status);
  return payload ?? {};
}

export async function getAuthProviderCapabilities(): Promise<AuthProviderCapabilities> {
  const payload = await authGet("/providers") as { providers?: Partial<AuthProviderCapabilities> };
  if (!payload.providers || typeof payload.providers.email !== "boolean") throw new AuthApiError("AUTH_INVALID_RESPONSE", "Invalid authentication capabilities.", 502);
  return { email: payload.providers.email, google: payload.providers.google === true, microsoft: payload.providers.microsoft === true };
}

export async function getAuthSession(): Promise<AuthSession | null> {
  const payload = await authGet("/get-session") as { user?: AuthSession["user"]; session?: AuthSession["session"] };
  return payload.user ? { user: payload.user, ...(payload.session ? { session: payload.session } : {}) } : null;
}

export const signOut = () => authRequest("/sign-out", {});

export function signInWithEmail(email: string, password: string, returnTo: string, locale: string) {
  return authRequest("/sign-in/email", { email, password, rememberMe: true, callbackURL: callbackUrl("/auth/callback", returnTo, locale).toString() }, locale);
}

export function signUpWithEmail(name: string, email: string, password: string, returnTo: string, locale: string) {
  return authRequest("/sign-up/email", { name, email, password, callbackURL: callbackUrl("/auth/callback", returnTo, locale).toString() }, locale);
}

export function signInWithSocial(provider: SocialProvider, returnTo: string, locale: string) {
  const errorCallback = callbackUrl("/signin", returnTo, locale);
  errorCallback.searchParams.set("authError", provider);
  return authRequest("/sign-in/social", { provider, callbackURL: callbackUrl("/auth/callback", returnTo, locale).toString(), errorCallbackURL: errorCallback.toString() }, locale);
}

export function requestMagicLink(email: string, returnTo: string, locale: string) {
  return authRequest("/sign-in/magic-link", { email, callbackURL: callbackUrl("/auth/callback", returnTo, locale).toString(), metadata: { locale } }, locale);
}

export function emailVerificationUrl(flow: "verify" | "magic", token: string, returnTo: string, locale: string): string {
  const callback = callbackUrl(flow === "magic" ? "/auth/callback" : "/signin", returnTo, locale);
  const endpoint = new URL(`/api/auth/${flow === "magic" ? "magic-link/verify" : "verify-email"}`, authApiBaseUrl());
  endpoint.searchParams.set("token", token);
  endpoint.searchParams.set("callbackURL", callback.toString());
  const errorCallback = callbackUrl("/signin", returnTo, locale);
  errorCallback.searchParams.set("authError", flow);
  endpoint.searchParams.set("errorCallbackURL", errorCallback.toString());
  return endpoint.toString();
}

export function requestPasswordReset(email: string, returnTo: string, locale: string) {
  const redirectTo = callbackUrl("/signin", returnTo, locale);
  redirectTo.searchParams.set("mode", "reset");
  return authRequest("/request-password-reset", { email, redirectTo: redirectTo.toString() }, locale);
}

export function resetPassword(newPassword: string, token: string) {
  return authRequest("/reset-password", { newPassword, token });
}

export function resendVerificationEmail(email: string, returnTo: string, locale: string) {
  return authRequest("/send-verification-email", { email, callbackURL: callbackUrl("/auth/callback", returnTo, locale).toString() }, locale);
}
